import React, { useRef, useState, useEffect } from "react";
import { useHistory } from "react-router-dom";

import timeSince from "utils/timeSince";
import formatDate from "utils/formatDate";
import { CURRENCY_SYMBOLS } from "constants/app";
import { LINK_TO_ROUTE, DEFAULT_DATE_FORMAT } from "constants/common";
import { trackAnalytics } from "services/analytics";
import { trackMOEEvent } from "utils/analytics";
import { navigateTo, navigateHandler, appendBetaInUrl } from "utils/redirection";
import { generateUrl } from "utils/generateUrl";
import Button from "components/button/button";

import Environment from "services/environment";

import { DROPDOWNS, MOE_HEADER, NOTIFICATION_TOAST_HEIGHT, SECONDS_IN_A_DAY } from "../constants";
import styles from "../postLoginHeader.scss";

const env = Environment.getEnv();

export default function Notification({
  source,
  activeDropdown,
  setActiveDropdown,
  notifications: { notificationList, totalUnReadCount },
  markNotificationRead,
  lob,
  partyId,
}) {
  const history = useHistory();
  const listRef = useRef();
  const [height, setHeight] = useState();

  useEffect(() => {
    getListSize();
  }, []);

  const getListSize = () => {
    const newHeight = listRef.current.clientHeight;
    setHeight(newHeight);
  };

  const viewAllNotifications = () => {
    const path = generateUrl(LINK_TO_ROUTE.ALL_NOTIFICATION, { lob }, { partyId });
    navigateHandler({ history, source, path });
    setActiveDropdown(null);
  };

  return (
    <li
      onClick={() => setActiveDropdown(DROPDOWNS.NOTIFICATION)}
      className={`${styles.drop_down} ${activeDropdown === DROPDOWNS.NOTIFICATION ? styles.active : ""}`}
    >
      <span
        className={`${styles.bell_icon} ${totalUnReadCount > 0 ? styles.active_notification : ""} ${
          activeDropdown === DROPDOWNS.NOTIFICATION ? styles.active_icon : styles.inactive_icon
        }`}
      >
        <i className="aeh-icon-outlined-notification"></i>
      </span>
      <div className={styles.dropbox}>
        <div className={`${styles.dropdown_card} ${styles.notify_card}`}>
          <div className={styles.card_header}>
            <div className={styles.header_content}>
              <h5>{totalUnReadCount} unread notifications</h5>
              <Button displayName="View All" variant="small_outlinebtn" onClick={viewAllNotifications} />
            </div>
            <div className={styles.close_icon}>
              <span />
              <span />
              <span />
            </div>
          </div>
          <div className={styles.card_content}>
            <div className={styles.scroll_content}>
              <ul className={`${styles.notify_list} ${height > NOTIFICATION_TOAST_HEIGHT ? styles.active : ""}`} ref={listRef}>
                {notificationList.map((notification, index) => (
                  <NotificationItem {...{ key: index, notification, markNotificationRead, partyId }} />
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </li>
  );
}

function NotificationItem({ notification, markNotificationRead, partyId }) {
  const { state, title, createDate, message, actionText, notificationId, url } = notification;

  const markReadAndRedirect = () => {
    faOnNotificationClick(notificationId, title);
    markNotificationRead({ partyId, notificationId }).finally(() => {
      trackMOEEvent(`${MOE_HEADER}_${DROPDOWNS.NOTIFICATION}_${title}_CLICK`).finally(() => navigateTo(appendBetaInUrl(url)));
    });
  };

  const formatMessage = (message) => {
    message = message.replace(new RegExp(`${Object.keys(CURRENCY_SYMBOLS).join("|")}`), function (matched) {
      return CURRENCY_SYMBOLS[matched];
    });
    return message;
  };

  const formatNotificationDateAndTime = (date) => {
    const notificationDateSeconds = date / 1000;
    const secondsInOneDay = SECONDS_IN_A_DAY;
    const currentTimeSeconds = new Date().getTime() / 1000;

    if (currentTimeSeconds - notificationDateSeconds < secondsInOneDay) {
      const [durationInteger, durationMeasurement] = timeSince(notificationDateSeconds).split(" ");
      return `${durationInteger}${durationMeasurement[0]} ago`;
    } else if (currentTimeSeconds - notificationDateSeconds < 2 * secondsInOneDay) {
      return `Yesterday`;
    } else {
      return `${formatDate(notificationDateSeconds, DEFAULT_DATE_FORMAT)}`;
    }
  };

  const faOnNotificationClick = (notificationId, title) => {
    trackAnalytics({
      eVar9: `b2b|aeh|notification|${title}`,
      prop18: notificationId,
    });
  };

  return (
    <li className={state === "UNREAD" ? styles.unread : styles.read}>
      <div className={styles.notification_item}>
        <div className={styles.payment_wrapper}>
          <span className={styles.status}>{title}</span>
          <div className={styles.time}>{formatNotificationDateAndTime(createDate)}</div>
        </div>
        <div className={styles.notification_text}>
          {formatMessage(message)}
          <div className={styles.links_wrapper}>
            <span className={styles.link_text} onClick={markReadAndRedirect}>
              {actionText}
              <i className={styles.arrowright} />
            </span>
          </div>
        </div>
      </div>
    </li>
  );
}
